/**
 * Handles the interactions of the switch reminder widget
 * @module embed/switch_reminder
 */

import { delegate } from '@/lib/events';
import { ERROR_MESSAGE } from '@/../packs/embed';
import { nameSelector } from './form';
import { withBusyButtonWhile } from '@/lib/busyButton';
import { sendEvent } from './analytics';

const SELECTOR = '.js-switch-reminder';
const SELECTOR_FIELD = '.js-switch-reminder [type="email"]';
const SELECTOR_SUBMIT = '.js-switch-reminder [type="submit"]';
const SELECTOR_SUCCESS = '.js-switch-reminder__success';
const SELECTOR_ERROR = '.js-switch-reminder__error';

const GA_ACTION_REMINDER = 'asked_reminder';

/**
 * Sets up the switch reminder interactions on elements requiring it
 * @param {HTMLElement} scope The element into which to look for the switch reminder widget
 * @param {*} apiClient The API client to send requests to the server with
 * @param {*} validator The form validator
 */
export default function setupSwitchReminders(scope, apiClient, validator) {
  Array.from(scope.querySelectorAll(SELECTOR)).forEach(function(el) {
    setupSwitchReminder(scope, el, apiClient, validator);
  });
}

function setupSwitchReminder(scope, el, apiClient, validator) {
  el.addEventListener(
    'click',
    delegate(SELECTOR_SUBMIT, event => {
      event.preventDefault();
      handleSubmission();
    })
  );

  el.addEventListener(
    'keydown',
    delegate(SELECTOR_FIELD, event => {
      if (event.keyCode == 13) {
        event.preventDefault();
        handleSubmission();
      }
    })
  );

  // Add required validation for the email field
  const emailField = el.querySelector(SELECTOR_FIELD);
  validator.addField(emailField.name, {
    validators: {
      notEmpty: {}
    }
  });
  // But disable it so it's only used when requesting the reminder
  validator.disableValidator(emailField.name, 'notEmpty');

  let isSubmitting;
  function handleSubmission() {
    if (!isSubmitting) {
      isSubmitting = true;
      const origin_url_field = scope.querySelector(
        '[name="submission[origin_url]"]'
      );

      const options = {
        slide_id: scope.querySelector('[name="slide_id"]').value,
        submission_id: scope.querySelector('[name="submission_id"]').value,
        email: scope.querySelector(nameSelector('remind_me_email')).value,
        origin_url: origin_url_field ? origin_url_field.value : null
      };
      el.querySelector(SELECTOR_SUCCESS).removeAttribute('aria-hidden');

      // Temporary enable the notEmpty validation, the time of sending the request
      validator.enableValidator(emailField.name, 'notEmpty');
      return validator
        .validateField(emailField.name)
        .then(status => {
          if (status == 'Valid') {
            sendEvent(el, GA_ACTION_REMINDER);
            return withBusyButtonWhile(
              el.querySelector(SELECTOR_SUBMIT),
              requestReminder(options, apiClient)
                .then(() => displaySuccess(el))
                .then(() => clearEmail(el))
                .catch(() => displayError(el))
            );
          }
          return status;
        })
        .finally(() => {
          isSubmitting = false;
          disableNotEmptyValidator(validator, el, emailField);
        });
    }
  }
}

// Simply using form validation API also removes the error feedback :'(
// This makes the disabling of the validation a bit trickier
function disableNotEmptyValidator(validator, el, emailField) {
  const messageEl = el.querySelector(
    '.fv-plugins-message-container [data-validator="notEmpty"]'
  );
  let message;
  if (messageEl) {
    message = messageEl.textContent;
  }
  validator.disableValidator(emailField.name, 'notEmpty');
  if (messageEl) {
    validator.updateFieldStatus(emailField.name, 'Invalid', 'notEmpty');
    el.querySelector(
      '.fv-plugins-message-container [data-validator="notEmpty"]'
    ).textContent = message;
  }
}

function requestReminder(options, apiClient) {
  if (!options.slide_id) return Promise.reject();
  if (!options.submission_id) return Promise.reject();

  return apiClient.requestReminder(options);
}

function displayError(el) {
  const errorEl = el.querySelector(SELECTOR_ERROR);
  errorEl.innerHTML = ERROR_MESSAGE;
  errorEl.setAttribute('aria-hidden', 'false');
}

function displaySuccess(el) {
  el.querySelector(SELECTOR_SUCCESS).setAttribute('aria-hidden', 'false');
  el.querySelector(SELECTOR_ERROR).removeAttribute('aria-hidden');
}

function clearEmail(el) {
  el.querySelector(SELECTOR_FIELD).value = '';
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/switch_reminder.js